import {LoadingButton} from '@mui/lab'
import {Box, Chip, Stack, Typography} from '@mui/material'
import type {FC, ReactElement} from 'react'
import BasicModal from '@shared/components/BasicModal'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'
import type {ViewAsStateInterface} from '@interfaces/user'

interface ViewAsModalProps {
  open: boolean
  onClose: () => void
  viewAs: ViewAsStateInterface
  onReset: () => void
  isResetting: boolean
}

const ViewAsModal: FC<ViewAsModalProps> = ({
  open,
  onClose,
  viewAs,
  onReset,
  isResetting,
}): ReactElement => {
  const [t] = useCustomTranslation('common')
  const teams = viewAs.teams ?? []

  return (
    <BasicModal
      open={open}
      onClose={onClose}
      title={t('view_as.modal_title')}
      maxWidth="480px"
      width="100%"
      footerAction={
        <LoadingButton
          onClick={onReset}
          loading={isResetting}
          variant="contained"
          color="error"
          data-testid="mbai-view-as-reset-button"
        >
          {t('view_as.reset_session')}
        </LoadingButton>
      }
    >
      <Stack spacing={2} data-testid="mbai-view-as-modal-content">
        {viewAs.organization ? (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('view_as.organization')}
            </Typography>
            <Typography variant="body1">{viewAs.organization.name}</Typography>
          </Box>
        ) : null}

        <Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{mb: 1}}>
            {t('view_as.teams')}
          </Typography>
          <Stack spacing={1.5}>
            {teams.map((team) => (
              <Box key={team.id}>
                <Typography variant="body1">{team.name}</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{mt: 0.5}}>
                  {team.roles.length > 0 ? (
                    team.roles.map((role) => (
                      <Chip key={role} label={role} size="small" sx={{mb: 0.5}} />
                    ))
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {t('view_as.no_roles')}
                    </Typography>
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      </Stack>
    </BasicModal>
  )
}

export default ViewAsModal
